// F1 App-Shell (TES-948): einzelner operativer SideNav-Link (Desktop).
// Aus SideNav extrahiert; traegt Badge-/Pulse-/Offline-Logik + data-tour.
// Logik 1:1 aus AppLayout. Badge-Rendering via NavBadge (entkoppelt).
// Render in kleine Sub-Komponenten + geteilte Nav-Bausteine zerlegt, damit keine
// Funktion die fallow-CRAP-Schwelle reisst — Verhalten/Markup unveraendert.
//
// TES-1137 (vgl. TES-1022/TES-1092 UserMenu): SideNavItemInner haengt unter
// TooltipTrigger asChild — Radix injiziert Handler/aria/ref als Props in das
// Kind. Die Kette SideNavItemInner -> SideNavItemLink/-Disabled MUSS daher
// forwardRef sein und unbekannte Props auf das DOM-Element spreaden, sonst
// loggt React auf jeder Seite eine Ref-Warning und der Tooltip oeffnet nie.
import { forwardRef, type HTMLAttributes, type Ref } from "react";
import { Link, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import type { ShellModel } from "./useShellModel";
import { navDataTour, type NavItem } from "./navConfig";
import { getNavBadgeInfo, type NavBadgeInfo } from "./navBadges";
import { NavBadgeDot, NavBadgePill } from "./NavBadge";
import { NavIcon, NavLabel, NavTooltip } from "./navLinkParts";

const ACTIVE_LINK =
  "border-l-4 border-secondary active-nav-glow bg-sidebar-accent text-sidebar-accent-foreground";
const INACTIVE_LINK =
  "hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-foreground";

function itemLayout(collapsed: boolean): string {
  return cn(
    "flex items-center gap-3 py-2.5 rounded-lg transition-all duration-200 relative",
    collapsed ? "px-2 justify-center" : "px-3",
  );
}

// Offline-deaktivierter Eintrag (kein Link, keine Badge). forwardRef + Prop-Spread
// sind Pflicht fuer den TooltipTrigger-asChild-Pfad (s. Kopf-Kommentar).
interface SideNavItemDisabledProps extends HTMLAttributes<HTMLDivElement> {
  item: NavItem;
  model: ShellModel;
  collapsed: boolean;
}

const SideNavItemDisabled = forwardRef<HTMLDivElement, SideNavItemDisabledProps>(
  ({ item, model, collapsed, className, ...props }, ref) => (
    <div
      ref={ref}
      data-tour={navDataTour(item.href)}
      aria-label={collapsed ? model.t(item.nameKey) : undefined}
      aria-disabled="true"
      className={cn(
        itemLayout(collapsed),
        "cursor-not-allowed opacity-40 pointer-events-auto text-sidebar-foreground/50",
        className,
      )}
      {...props}
    >
      <NavIcon item={item} collapsed={collapsed} />
      <NavLabel hidden={collapsed} className="flex-1">
        {model.t(item.nameKey)}
      </NavLabel>
    </div>
  ),
);
SideNavItemDisabled.displayName = "SideNavItemDisabled";

// Aktiver Link inkl. Badge (collapsed Dot / expanded Pill). forwardRef + Prop-Spread
// (react-router Link reicht den Ref an das <a> durch).
interface SideNavItemLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  item: NavItem;
  model: ShellModel;
  collapsed: boolean;
  badge: NavBadgeInfo;
  isActive: boolean;
}

// TES-1664: pre-existing collapsed/badge link states; this PR only preserves accessible names.
// fallow-ignore-next-line complexity
const SideNavItemLink = forwardRef<HTMLAnchorElement, SideNavItemLinkProps>(
  // fallow-ignore-next-line complexity
  ({ item, model, collapsed, badge, isActive, className, ...props }, ref) => (
    <Link
      ref={ref}
      to={item.href}
      data-tour={navDataTour(item.href)}
      aria-label={collapsed ? model.t(item.nameKey) : undefined}
      className={cn(itemLayout(collapsed), isActive ? ACTIVE_LINK : INACTIVE_LINK, className)}
      {...props}
    >
      <div className="relative">
        <NavIcon item={item} collapsed={collapsed} />
        {collapsed && <NavBadgeDot badge={badge} className="-top-1 -right-1" />}
      </div>
      {!collapsed && (
        <>
          <NavLabel className="flex-1">{model.t(item.nameKey)}</NavLabel>
          <NavBadgePill badge={badge} href={item.href} />
        </>
      )}
    </Link>
  ),
);
SideNavItemLink.displayName = "SideNavItemLink";

interface SideNavItemProps {
  item: NavItem;
  model: ShellModel;
  collapsed: boolean;
}

// Innerer Link bzw. disabled-Div ohne Tooltip-Wrapper. forwardRef + Prop-Spread,
// damit TooltipTrigger asChild seine Handler/aria/ref bis ans DOM durchreichen kann.
interface SideNavItemInnerProps extends HTMLAttributes<HTMLElement> {
  item: NavItem;
  model: ShellModel;
  collapsed: boolean;
  badge: NavBadgeInfo;
  isActive: boolean;
  isOfflineDisabled: boolean;
}

const SideNavItemInner = forwardRef<HTMLElement, SideNavItemInnerProps>(
  ({ item, model, collapsed, badge, isActive, isOfflineDisabled, ...props }, ref) => {
    if (isOfflineDisabled) {
      return (
        <SideNavItemDisabled
          ref={ref as Ref<HTMLDivElement>}
          item={item}
          model={model}
          collapsed={collapsed}
          {...props}
        />
      );
    }
    return (
      <SideNavItemLink
        ref={ref as Ref<HTMLAnchorElement>}
        item={item}
        model={model}
        collapsed={collapsed}
        badge={badge}
        isActive={isActive}
        {...props}
      />
    );
  },
);
SideNavItemInner.displayName = "SideNavItemInner";

// Tooltip-Text: Offline-Hinweis hat Vorrang, sonst Badge-Zusammenfassung (1:1 aus AppLayout).
function itemTooltipText(model: ShellModel, badge: NavBadgeInfo, isOfflineDisabled: boolean) {
  if (isOfflineDisabled) return model.t("nav.offlineDisabled");
  return model.t("nav.badgeTooltip", { open: badge.badgeCount, overdue: badge.overdue });
}

export function SideNavItem({ item, model, collapsed }: SideNavItemProps) {
  const location = useLocation();
  const { isOnline } = model;
  const isActive = location.pathname === item.href;
  const isOfflineDisabled = !isOnline && !item.offlineEnabled;
  const badge = getNavBadgeInfo(item.href, model);
  const showTooltip = isOfflineDisabled || badge.showBadge;

  const inner = (
    <SideNavItemInner
      item={item}
      model={model}
      collapsed={collapsed}
      badge={badge}
      isActive={isActive}
      isOfflineDisabled={isOfflineDisabled}
    />
  );

  if (!showTooltip) return inner;

  return (
    <NavTooltip collapsed={collapsed} text={itemTooltipText(model, badge, isOfflineDisabled)}>
      {inner}
    </NavTooltip>
  );
}
